import React, { useEffect, useRef, useState } from 'react';
import { DownloadIcon, ExternalLinkIcon, FolderInputIcon, InfoIcon, MoreHorizontalIcon, PencilIcon, RotateCcwIcon, StarIcon, Trash2Icon, XCircleIcon } from 'lucide-react';
import type { FsNode } from '../../types/files';
export interface ItemActions {
  onOpen: (node: FsNode) => void;
  onDetails: (node: FsNode) => void;
  onDownload: (node: FsNode) => void;
  onToggleStar: (node: FsNode) => void;
  onRename: (node: FsNode) => void;
  onMove: (node: FsNode) => void;
  onTrash: (node: FsNode) => void;
  onRestore: (node: FsNode) => void;
  onDeleteForever: (node: FsNode) => void;
}
interface ItemMenuProps {
  node: FsNode;
  isTrash: boolean;
  actions: ItemActions;
}
export function ItemMenu({
  node,
  isTrash,
  actions
}: ItemMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  const run = (action: (node: FsNode) => void) => () => {
    setOpen(false);
    action(node);
  };
  const items = isTrash ? [{
    label: 'Restore',
    icon: RotateCcwIcon,
    onSelect: run(actions.onRestore)
  }, {
    label: 'Delete forever',
    icon: XCircleIcon,
    onSelect: run(actions.onDeleteForever),
    danger: true
  }] : [{
    label: node.kind === 'folder' ? 'Open folder' : 'Open preview',
    icon: ExternalLinkIcon,
    onSelect: run(actions.onOpen)
  }, {
    label: 'Details',
    icon: InfoIcon,
    onSelect: run(actions.onDetails)
  }, {
    label: node.kind === 'folder' ? 'Download as zip' : 'Download',
    icon: DownloadIcon,
    onSelect: run(actions.onDownload)
  }, {
    label: node.starred ? 'Remove star' : 'Add star',
    icon: StarIcon,
    onSelect: run(actions.onToggleStar)
  }, {
    label: 'Rename',
    icon: PencilIcon,
    onSelect: run(actions.onRename)
  }, {
    label: 'Move to…',
    icon: FolderInputIcon,
    onSelect: run(actions.onMove)
  }, {
    label: 'Move to Recycle Bin',
    icon: Trash2Icon,
    onSelect: run(actions.onTrash),
    danger: true
  }];
  return <div ref={containerRef} className="relative" data-id="element-185" data-mp="z5hvhf7iyx1v">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={`Actions for ${node.name}`} onClick={event => {
      event.stopPropagation();
      setOpen(current => !current);
    }} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-186" data-mp="z46rbea63na8">
        <MoreHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-187" data-mp="xnjbn4sdgl3p" />
      </button>
      {open && <div role="menu" className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded border border-slate-200 bg-white py-1 shadow-lg" data-id="element-188" data-mp="1leyfj1zsrah2">
          {items.map(item => {
        const Icon = item.icon;
        return <button key={item.label} type="button" role="menuitem" onClick={event => {
          event.stopPropagation();
          item.onSelect();
        }} className={['flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition focus:outline-none', item.danger ? 'text-red-700 hover:bg-red-50' : 'text-slate-700 hover:bg-slate-100'].join(' ')} data-id="element-189" data-mp="1gn6xxgymcf32">
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-190" data-mp="bj32pmsganw8" />
                {item.label}
              </button>;
      })}
        </div>}
    </div>;
}